import { trackEvent, trackButtonClick } from "@/utils/analytics";
import logger from "@/utils/logger";

/**
 * Checkout Analytics Utility
 * Provides consistent event tracking for the Stripe checkout integration
 */

// Event names for checkout flow
export const CHECKOUT_EVENTS = {
  // Checkout initiation
  CHECKOUT_INITIATED: "checkout_initiated",
  PRICING_TIER_SELECTED: "pricing_tier_selected",
  
  // Form interactions
  CHECKOUT_FORM_OPENED: "checkout_form_opened",
  CHECKOUT_FORM_FIELD_FOCUSED: "checkout_form_field_focused",
  CHECKOUT_FORM_VALIDATION_ERROR: "checkout_form_validation_error",
  CHECKOUT_FORM_SUBMITTED: "checkout_form_submitted",
  
  // Payment processing
  PAYMENT_PROCESSING_STARTED: "payment_processing_started",
  PAYMENT_SUCCESS: "payment_success",
  PAYMENT_FAILED: "payment_failed",
  
  // Account creation
  ACCOUNT_CREATION_STARTED: "account_creation_started",
  ACCOUNT_CREATION_SUCCESS: "account_creation_success",
  ACCOUNT_CREATION_FAILED: "account_creation_failed",
  
  // Authentication flow
  AUTH_FLOW_STARTED: "auth_flow_started",
  AUTH_FLOW_SUCCESS: "auth_flow_success",
  AUTH_FLOW_FAILED: "auth_flow_failed",
  
  // Completion
  CHECKOUT_FLOW_COMPLETED: "checkout_flow_completed",
  
  // User interactions
  CHECKOUT_MODAL_CLOSED: "checkout_modal_closed",
  CHECKOUT_RETRY_ATTEMPTED: "checkout_retry_attempted",
};

// Pricing tier mapping
export const PRICING_TIERS = {
  MONTHLY: "monthly",
  ANNUAL: "annual",
};

// Error categories for better analysis
export const ERROR_CATEGORIES = {
  PAYMENT: "payment_error",
  VALIDATION: "validation_error",
  NETWORK: "network_error",
  ACCOUNT_CREATION: "account_creation_error",
  AUTH: "authentication_error",
  UNKNOWN: "unknown_error",
};

/**
 * Track checkout initiation
 * @param {string} pricingTier - The selected pricing tier
 * @param {string} source - Where the checkout was initiated from
 * @param {Object} additionalData - Additional tracking data
 */
export const trackCheckoutInitiated = (pricingTier, source = "pricing_section", additionalData = {}) => {
  const eventData = {
    pricing_tier: pricingTier,
    source: source,
    timestamp: new Date().toISOString(),
    session_id: generateSessionId(),
    ...additionalData,
  };

  logger.log("Checkout initiated", eventData);
  return trackEvent(CHECKOUT_EVENTS.CHECKOUT_INITIATED, eventData);
};

/**
 * Track pricing tier selection
 * @param {string} pricingTier - The selected pricing tier
 * @param {string} previousTier - Previously selected tier (if any)
 */
export const trackPricingTierSelected = (pricingTier, previousTier = null) => {
  const eventData = {
    pricing_tier: pricingTier,
    previous_tier: previousTier,
    timestamp: new Date().toISOString(),
  };

  return trackEvent(CHECKOUT_EVENTS.PRICING_TIER_SELECTED, eventData);
};

/**
 * Track checkout form interactions
 * @param {string} action - The form action (opened, field_focused, submitted, etc.)
 * @param {Object} formData - Form interaction data
 */
export const trackCheckoutFormInteraction = (action, formData = {}) => {
  const eventData = {
    form_action: action,
    timestamp: new Date().toISOString(),
    ...formData,
  };

  switch (action) {
    case "opened":
      return trackEvent(CHECKOUT_EVENTS.CHECKOUT_FORM_OPENED, eventData);
    case "field_focused":
      return trackEvent(CHECKOUT_EVENTS.CHECKOUT_FORM_FIELD_FOCUSED, eventData);
    case "validation_error":
      return trackEvent(CHECKOUT_EVENTS.CHECKOUT_FORM_VALIDATION_ERROR, eventData);
    case "submitted":
      return trackEvent(CHECKOUT_EVENTS.CHECKOUT_FORM_SUBMITTED, eventData);
    default:
      logger.warn("Unknown form action", { action, formData });
      return false;
  }
};

/**
 * Track payment processing events
 * @param {string} status - Payment status (started, success, failed)
 * @param {Object} paymentData - Payment-related data
 */
export const trackPaymentProcessing = (status, paymentData = {}) => {
  const eventData = {
    payment_status: status,
    timestamp: new Date().toISOString(),
    ...paymentData,
  };

  switch (status) {
    case "started":
      return trackEvent(CHECKOUT_EVENTS.PAYMENT_PROCESSING_STARTED, eventData);
    case "success":
      return trackEvent(CHECKOUT_EVENTS.PAYMENT_SUCCESS, eventData);
    case "failed":
      return trackEvent(CHECKOUT_EVENTS.PAYMENT_FAILED, eventData);
    default:
      logger.warn("Unknown payment status", { status, paymentData });
      return false;
  }
};

/**
 * Track account creation events
 * @param {string} status - Account creation status (started, success, failed)
 * @param {Object} accountData - Account creation data
 */
export const trackAccountCreation = (status, accountData = {}) => {
  const eventData = {
    account_status: status,
    timestamp: new Date().toISOString(),
    ...accountData,
  };

  switch (status) {
    case "started":
      return trackEvent(CHECKOUT_EVENTS.ACCOUNT_CREATION_STARTED, eventData);
    case "success":
      return trackEvent(CHECKOUT_EVENTS.ACCOUNT_CREATION_SUCCESS, eventData);
    case "failed":
      return trackEvent(CHECKOUT_EVENTS.ACCOUNT_CREATION_FAILED, eventData);
    default:
      logger.warn("Unknown account creation status", { status, accountData });
      return false;
  }
};

/**
 * Track authentication flow events
 * @param {string} status - Auth flow status (started, success, failed)
 * @param {Object} authData - Authentication data
 */
export const trackAuthFlow = (status, authData = {}) => {
  const eventData = {
    auth_status: status,
    timestamp: new Date().toISOString(),
    ...authData,
  };

  switch (status) {
    case "started":
      return trackEvent(CHECKOUT_EVENTS.AUTH_FLOW_STARTED, eventData);
    case "success":
      return trackEvent(CHECKOUT_EVENTS.AUTH_FLOW_SUCCESS, eventData);
    case "failed":
      return trackEvent(CHECKOUT_EVENTS.AUTH_FLOW_FAILED, eventData);
    default:
      logger.warn("Unknown auth flow status", { status, authData });
      return false;
  }
};

/**
 * Track checkout flow completion
 * @param {Object} completionData - Data about the completed flow
 */
export const trackCheckoutFlowCompleted = (completionData = {}) => {
  const eventData = {
    completion_time: new Date().toISOString(),
    ...completionData,
  };

  return trackEvent(CHECKOUT_EVENTS.CHECKOUT_FLOW_COMPLETED, eventData);
};

/**
 * Track checkout errors with categorization
 * @param {Error} error - The error object
 * @param {string} context - Where the error occurred
 * @param {string} category - Error category for analysis
 */
export const trackCheckoutError = (error, context, category = ERROR_CATEGORIES.UNKNOWN) => {
  const eventData = {
    error_message: error.message,
    error_context: context,
    error_category: category,
    error_stack: error.stack ? error.stack.substring(0, 500) : null, // Limit stack trace
    timestamp: new Date().toISOString(),
  };

  logger.error("Checkout error tracked", eventData);
  return trackEvent("checkout_error", eventData);
};

/**
 * Track user interactions during checkout
 * @param {string} interaction - Type of interaction
 * @param {Object} interactionData - Interaction details
 */
export const trackCheckoutInteraction = (interaction, interactionData = {}) => {
  const eventData = {
    interaction_type: interaction,
    timestamp: new Date().toISOString(),
    ...interactionData,
  };

  switch (interaction) {
    case "modal_closed":
      return trackEvent(CHECKOUT_EVENTS.CHECKOUT_MODAL_CLOSED, eventData);
    case "retry_attempted":
      return trackEvent(CHECKOUT_EVENTS.CHECKOUT_RETRY_ATTEMPTED, eventData);
    default:
      return trackButtonClick(`checkout_${interaction}`, eventData);
  }
};

/**
 * Generate a unique session ID for tracking the checkout flow
 * @returns {string} Session ID
 */
const generateSessionId = () => {
  return `checkout_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Calculate time difference for performance tracking
 * @param {Date|string} startTime - Start time
 * @param {Date|string} endTime - End time (defaults to now)
 * @returns {number} Time difference in milliseconds
 */
export const calculateTimeDifference = (startTime, endTime = new Date()) => {
  const start = new Date(startTime);
  const end = new Date(endTime);
  return end.getTime() - start.getTime();
};

/**
 * Format error for analytics tracking
 * @param {Error} error - Error object
 * @param {string} context - Context where error occurred
 * @returns {Object} Formatted error data
 */
export const formatErrorForAnalytics = (error, context) => {
  return {
    message: error.message,
    name: error.name,
    context: context,
    timestamp: new Date().toISOString(),
    // Don't include full stack trace in analytics for privacy
    has_stack: !!error.stack,
  };
};

/**
 * Track conversion funnel step completion
 * @param {string} step - Funnel step name
 * @param {Object} stepData - Step completion data
 */
export const trackFunnelStep = (step, stepData = {}) => {
  const eventData = {
    funnel_step: step,
    timestamp: new Date().toISOString(),
    ...stepData,
  };

  return trackEvent("checkout_funnel_step", eventData);
};